import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import "./GlossaryTab.scss";

import { GlossaryFileSupport, GlossaryFileSupportResponse } from "../../../../../@api";

import { IGlossaryFileSupportExtended } from "../../../../features/translation/TranslationHeader/GlossarySelection/GlossarySelection.types";
import { translationKeys } from "../../../../models/app-translation-keys";
import { AllLanguageOptions } from "../../../../models/constants/app-language-options.const";
import { ILanguageOption } from "../../../../models/interfaces";
import { useGlossaryService } from "../../../../services/ServiceHooks";
import { TextMarkdown } from "../../../Shared/Components";
import { AddGlossaryButton } from "./AddGlossaryButton/AddGlossaryButton";
import GlossaryTable from "./GlossaryTable/GlossaryTable";

const RawGlossaryTab: FunctionComponent = () => {
  const { t } = useTranslation();

  const glossaryService = useGlossaryService();

  const [isLoading, setIsLoading] = useState(false);
  const [glossaries, setGlossaries] = useState<IGlossaryFileSupportExtended[]>([]);

  const requestGlossaryInfos = async () => {
    setIsLoading(true);
    const response: GlossaryFileSupportResponse = await glossaryService.getGlossaryFileInfos();

    const glossariesExtended = response?.glossaryFileSupports?.map(glossary => ({
      ...glossary,
      supportedLanguageOptions: glossary.supportedLanguages
        .map(languageCode => AllLanguageOptions.find(langOption => langOption.code.includes(languageCode)))
        .filter((langOption): langOption is ILanguageOption => !!langOption),
    }));

    setGlossaries(glossariesExtended);

    setIsLoading(false);
  };

  useEffect(() => {
    if (!glossaries.length) {
      requestGlossaryInfos();
    }
  }, []);

  const removeEntryFromTable = (entry: GlossaryFileSupport) => {
    setGlossaries(prevInfos => prevInfos.filter(info => info.glossaryId !== entry.glossaryId));
  };
  return (
    <div className="glossary-tab">
      <div className="glossary-header">
        <h1>{t(translationKeys.adminArea.glossary.tabHeader)}</h1>

        <TextMarkdown>{t(translationKeys.adminArea.glossary.tabDescriptionMarkdown)}</TextMarkdown>
      </div>

      <div className="glossary">
        <div className="glossary-controls">
          <AddGlossaryButton onUploadCompleted={requestGlossaryInfos} />
        </div>

        <GlossaryTable
          glossaryInfos={glossaries}
          isLoading={isLoading}
          onReloadTable={requestGlossaryInfos}
          onDeleteEntry={removeEntryFromTable}
        />
      </div>
    </div>
  );
};

export const GlossaryTab = observer(RawGlossaryTab);
